import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { Box } from '@nimbus-ds/components';
import { Page } from '@nimbus-ds/patterns';
import { useNavTabs } from 'App/components/Navtabs';
import { IonPageStratus, useResponsive } from 'commons/components';
import { useStorage, useToast } from 'commons/hooks';
import { logError } from 'commons/logger';
import { useGetStoreInfo } from 'domains/Auth/hooks';
import { useBillingPlans } from 'domains/Billing/BillingPlans/hooks';
import { AppLoading } from 'domains/PartnersApps/components/EmbeddedApp';
import { RECOMMEND_PLAN_COMPLETED } from 'domains/PreOnboarding/hooks';
import {
  trackingOnboardingRecommendedPlanFreeClick,
  trackingOnboardingRecommendedPlanScreenShow,
  trackingOnboardingRecommendedPlanTestClick,
  trackingOnboardingRecommendedSeeOtherPlansClick,
} from 'domains/PreOnboarding/tracking';
import useTranslationPreOnboarding from 'domains/PreOnboarding/useTranslationPreOnboarding';
import { PlanPageBodyDesktop, PlanPageBodyMobile } from './components';
import { useGetPlanRecommendationsQuery } from './recommendPlanApi';
import { SelectedPlanType } from './types';

interface RecommendPlanPageProps {
  onError: () => void;
  onCompleted: (redirectToPlans?: boolean) => void;
}

function RecommendPlanPage({
  onCompleted,
  onError,
}: Readonly<RecommendPlanPageProps>) {
  const { addToast } = useToast();
  const { isMobile } = useResponsive();
  const t = useTranslationPreOnboarding('recommendPlan');
  const { hideSideBar, hideNavTabs, showNavTabs } = useNavTabs();

  const { id } = useGetStoreInfo();
  const localStorageItem = `${RECOMMEND_PLAN_COMPLETED}-${id}`;
  const [recommendPlanCompleted, , completedLoaded] = useStorage(
    localStorageItem,
    false,
  );
  const { updateBillingPlan } = useBillingPlans(false);
  const {
    data: planRecommendationsResponse,
    isLoading,
    isError,
    isUninitialized,
  } = useGetPlanRecommendationsQuery(undefined, {
    skip: recommendPlanCompleted || !completedLoaded,
  });
  const [showError, setShowError] = useState(false);

  useLayoutEffect(() => {
    hideSideBar();
    hideNavTabs();
    return () => showNavTabs();
  }, [hideSideBar, hideNavTabs, showNavTabs]);

  useEffect(() => {
    if (
      !isUninitialized &&
      !isLoading &&
      (!planRecommendationsResponse || isError)
    )
      onError();
  }, [
    planRecommendationsResponse,
    isError,
    onError,
    isUninitialized,
    isLoading,
  ]);

  useEffect(() => {
    trackingOnboardingRecommendedPlanScreenShow();
  }, []);

  useEffect(() => {
    if (showError) {
      setShowError(false);
      addToast({
        label: t('error'),
        appearance: 'danger',
      });
    }
  }, [showError, addToast, t]);

  const onResolvePlan = useCallback(
    async (planType: SelectedPlanType) => {
      if (!planRecommendationsResponse) return;
      const planId = planRecommendationsResponse.upgrade.planId;
      let redirectToPlans = false;
      switch (planType) {
        case 'upgrade':
          trackingOnboardingRecommendedPlanTestClick();
          try {
            await updateBillingPlan(planId);
          } catch (error) {
            setShowError(true);
            logError(`Error updating plan Onboarding`, error);
            return;
          }
          break;
        case 'free':
          trackingOnboardingRecommendedPlanFreeClick();
          break;
        case 'none':
          trackingOnboardingRecommendedSeeOtherPlansClick();
          redirectToPlans = true;
          break;
      }

      onCompleted(redirectToPlans);
    },
    [planRecommendationsResponse, onCompleted, updateBillingPlan],
  );

  if (isError && !isLoading && !isUninitialized) return null;
  if (isLoading || !planRecommendationsResponse || !completedLoaded)
    return <AppLoading />;

  return (
    <IonPageStratus>
      <Box
        width="100vw"
        height="100vh"
        backgroundColor={isMobile ? 'neutral-background' : 'neutral-surface'}
        position="fixed"
        top="0"
        left="0"
        display="flex"
        alignItems="center"
      >
        <Page maxWidth="800px" my="8">
          <Page.Header
            paddingX="4"
            title={t('title')}
            subtitle={t('subtitle')}
          />

          <Page.Body
            paddingX="4"
            paddingTop="none"
            display="flex"
            flexDirection="column"
            gap="8"
          >
            {isMobile ? (
              <PlanPageBodyMobile
                plans={planRecommendationsResponse}
                onPlanSelect={onResolvePlan}
              />
            ) : (
              <PlanPageBodyDesktop
                plans={planRecommendationsResponse}
                onPlanSelect={onResolvePlan}
              />
            )}
          </Page.Body>
        </Page>
      </Box>
    </IonPageStratus>
  );
}

export default RecommendPlanPage;
